import { Autocomplete, Box, Card, Modal, TextField } from '@mui/material';
import { useRef } from 'react';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useTranslation } from 'react-i18next';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { OptionType } from '@frontend/features/common/types';
import { useQueryClient } from 'react-query';
import { useGetTeamsByTokensAgencies } from '../../queries';

interface Props {
  isOpen: boolean;
  userId: string;
  agencyId: number;
  onClose: () => void;
  queryKey?: unknown[];
}

export const LinkTeamToUser = ({ isOpen, onClose, userId, queryKey, agencyId }: Props) => {
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const [autocompleteValue, setAutocompleteValue] = useDebouncedState<string>('', 500);
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  
  const { data: teamsList } = useGetTeamsByTokensAgencies({
    pageSize: 10,
    currentPage: 1,
    search: autocompleteValue,
  });

  const listOptions =
    teamsList?.items?.map((team) => ({
      value: String(team.id),
      label: team.name
    })) ?? [];

  const handleChange = async (teamItem: OptionType | null) => {
    try {
      await client.post(`/team/${teamItem?.value}/users/${userId}`);

      addToast({
        message: t('teams.linkUserToTeam.success'),
        color: 'success',
      });

      queryKey && queryClient.invalidateQueries(queryKey);
    } catch (_e) {
      addToast({
        message: t('teams.linkUserToTeam.success'),
        color: 'error',
      });
    } finally {
      onClose();
    }
  };

  const handleCancel = () => {
    setAutocompleteValue('');
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          p: 2,
        }}
        elevation={4}
      >
        <Box ref={formRef} component="form" display="flex" gap={2}>
          <Autocomplete
            fullWidth
            options={listOptions}
            getOptionLabel={(option) => option.label}
            onChange={(_event: React.SyntheticEvent, newValue: OptionType | null) => {
              handleChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                onChange={(e) => {
                  setAutocompleteValue(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.nativeEvent.key === 'Escape' && handleCancel();
                }}
                variant="outlined"
              />
            )}
          />
        </Box>
      </Card>
    </Modal>
  );
};
